import { Timestamp } from '../models/assignment.model';

function toSeconds(timestamp?: Timestamp | string): number | null {
  if (!timestamp) return null;

  if (typeof timestamp === 'string') {
    const normalized = timestamp.trim();
    if (!/^\d+$/.test(normalized)) return null;

    const parsed = parseInt(normalized, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  const s = (timestamp as Timestamp).seconds;
  if (typeof s === 'string') {
    const parsed = parseInt(s, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (typeof s === 'number') {
    return s;
  }
  return null;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function normalizeFormattedDate(dateValue: string): string {
  const normalized = dateValue.trim();
  if (!normalized) return '';

  const dateOnly = normalized.split(/[T\s]/)[0];

  const ddmmyyyy = dateOnly.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ddmmyyyy) {
    const [, day, month, year] = ddmmyyyy;
    return `${pad(Number(day))}/${pad(Number(month))}/${year}`;
  }

  const yyyymmdd = dateOnly.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (yyyymmdd) {
    const [, year, month, day] = yyyymmdd;
    return `${pad(Number(day))}/${pad(Number(month))}/${year}`;
  }

  const ddmmyyyyDash = dateOnly.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (ddmmyyyyDash) {
    const [, day, month, year] = ddmmyyyyDash;
    return `${pad(Number(day))}/${pad(Number(month))}/${year}`;
  }

  return '';
}

export class DateFormatter {
  static formatDate(timestamp?: Timestamp | string): string {
    const seconds = toSeconds(timestamp);
    if (seconds === null) return '';

    const d = new Date(seconds * 1000);
    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  static formatDatePreservingDay(timestamp?: Timestamp | string): string {
    if (typeof timestamp === 'string') {
      const formattedDate = normalizeFormattedDate(timestamp);
      if (formattedDate) return formattedDate;
      return timestamp.trim().replace(/-/g, '/');
    }

    const seconds = toSeconds(timestamp);
    if (seconds === null) return '';

    const d = new Date(seconds * 1000);
    const day = pad(d.getUTCDate());
    const month = pad(d.getUTCMonth() + 1);
    const year = d.getUTCFullYear();

    return `${day}/${month}/${year}`;
  }

  static formatTime(timestamp?: Timestamp | string): string {
    const seconds = toSeconds(timestamp);
    if (seconds === null) return '';
    const d = new Date(Number(seconds) * 1000);
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    return `${hours}:${minutes}`;
  }

  static parseDate(dateStr: string): Date {
    const [day, month, year] = dateStr.split('/').map((n) => parseInt(n, 10));
    return new Date(year, (month || 1) - 1, day || 1);
  }

  static compareDates(
    dateStrA: string,
    dateStrB: string,
    direction: 1 | -1
  ): number {
    const dateA = this.parseDate(dateStrA);
    const dateB = this.parseDate(dateStrB);

    if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
      return 0;
    }
    if (dateA < dateB) return -direction;
    if (dateA > dateB) return direction;
    return 0;
  }
}
